/**
 * -----------------------------------------------------------------------------
 * Project  : ReelConsensus app - CLZ Migration Page
 * File     : CLZMigration.js
 * Author   : Zachary Dry
 * Created  : 2025-01-27
 * Updated  : 2025-01-27
 * Version  : 1.0
 * License: Proprietary (Company Confidential)
 * -----------------------------------------------------------------------------
 * Description:
 *   Platform Owner only page for migrating movie collection data to Physical Media Collection.
 *   Provides step-by-step guidance and real-time migration progress.
 * -----------------------------------------------------------------------------
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  CardActions,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Pagination
} from '@mui/material';
import {
  CloudDownload,
  CheckCircle,
  Error,
  ExpandMore,
  PlayArrow,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { buildApiUrl } from '../services/api';

const CLZMigration = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [limit, setLimit] = useState(10);
  const [, setIsMigrating] = useState(false);
  const [migrationResult, setMigrationResult] = useState(null);
  const [extractedData, setExtractedData] = useState([]);
  const [extractionResult, setExtractionResult] = useState(null);
  const [isExtracting, setIsExtracting] = useState(false);
  const [extractionProgress, setExtractionProgress] = useState(0);
  const [previewPage, setPreviewPage] = useState(1);
  const [previewPageSize] = useState(10);

  // Calculate pagination for preview
  const totalPages = Math.ceil(extractedData.length / previewPageSize);
  const startIndex = (previewPage - 1) * previewPageSize;
  const endIndex = startIndex + previewPageSize;
  const paginatedData = extractedData.slice(startIndex, endIndex);

  const steps = [
    'XML Import Ready',
    'Extract Data Preview',
    'Migrate to Database',
    'Review Results'
  ];

  // Check if user is platform owner
  useEffect(() => {
    if (user && user.role !== 'owner') {
      navigate('/');
    }
  }, [user, navigate]);


  const handleMigration = async () => {
    setIsMigrating(true);
    setActiveStep(2);
    
    try {
      // Use XML endpoint for migration
      const endpoint = '/api/physical-media/migrate-from-clz-xml';
      const apiUrl = buildApiUrl(endpoint);
      console.log('Making migration API call to:', apiUrl);
        
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          limit,
          previewOnly: false  // This is the key difference - actually save to database
        })
      });

      console.log('Migration response status:', response.status);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('Migration API Error Response:', errorText);
        throw new Error(`Migration API Error: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      
      
      if (response.ok) {
        setMigrationResult(result);
        setActiveStep(3);
      } else {
        throw new Error(result.error || 'Migration failed');
      }
    } catch (error) {
      setMigrationResult({ error: error.message });
      setActiveStep(3);
    } finally {
      setIsMigrating(false);
    }
  };

  const handleExtractPreview = async () => {
    setIsExtracting(true);
    setExtractionProgress(0);
    setActiveStep(1);
    
    let progressInterval;
    
    try {
      // Simulate extraction progress
      progressInterval = setInterval(() => {
        setExtractionProgress(prev => {
          if (prev >= 90) {
            clearInterval(progressInterval);
            return 90;
          }
          return prev + 10;
        });
      }, 500);

      // Use XML endpoint
      const endpoint = '/api/physical-media/migrate-from-clz-xml';

      const apiUrl = buildApiUrl(endpoint);
      console.log('Making API call to:', apiUrl);
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          limit,
          previewOnly: true
        })
      });

      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error Response:', errorText);
        throw new Error(`API Error: ${response.status} - ${errorText}`);
      }

      const result = await response.json();
      
      // Debug: Log the full response
      console.log('Full API response:', result);
      console.log('Response status:', response.status);
      console.log('Response ok:', response.ok);
      
      if (response.ok) {
        // Handle both original and hybrid response formats
        const movies = result.movies || result.previewData || [];
        console.log('Extracted movies:', movies);
        console.log('Movies length:', movies.length);
        
        setExtractedData(movies);
        setExtractionResult(result);
        setExtractionProgress(100);
        setPreviewPage(1); // Reset to first page when new data is loaded
        setTimeout(() => clearInterval(progressInterval), 1000);
        
        // Debug: Log state updates
        console.log('Setting extractedData to:', movies);
        console.log('Movies array length:', movies.length);
        
        // Log the response for debugging
        console.log('Extraction successful:', {
          totalMovies: movies.length,
          summary: result.summary,
          message: result.message
        });
      } else {
        throw new Error(result.error || 'Extraction failed');
      }
    } catch (error) {
      console.error('Extraction error:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        response: error.response
      });
      
      // Only reset data if it's a real error, not a successful response
      if (error.message.includes('Extraction failed') || error.message.includes('Network')) {
        setExtractedData([]);
        setExtractionProgress(0);
      }
    } finally {
      if (progressInterval) {
        clearInterval(progressInterval);
      }
      setIsExtracting(false);
    }
  };


  if (!user || user.role !== 'owner') {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <Typography variant="h4" color="error">
          Access Denied
        </Typography>
        <Typography variant="body1" sx={{ mt: 2 }}>
          This page is only accessible to Platform Owners.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
      <Typography variant="h4" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <CloudDownload color="primary" />
        Collection Migration
      </Typography>
      
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Migrate your movie collection to your Physical Media Collection database.
      </Typography>


      <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Step 1: Get CLZ Credentials */}
      {activeStep === 0 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Step 1: XML Import Ready
            </Typography>
            
            <Alert severity="success" sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                XML Import Ready!
              </Typography>
              <Typography>
                Your CLZ collection has been exported to XML and is ready for import. 
                No credentials are needed - the system will read directly from the XML file.
              </Typography>
            </Alert>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Alert severity="info">
                  <Typography variant="body2">
                    The XML file contains all your movie data including titles, years, formats, 
                    purchase information, and cover images. Click "Extract Preview" to see the first few movies.
                  </Typography>
                </Alert>
              </Grid>
              

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Migration Limit"
                  type="number"
                  value={limit}
                  onChange={(e) => setLimit(parseInt(e.target.value) || 10)}
                  inputProps={{ min: 1, max: 50 }}
                  helperText="Number of movies to migrate (1-50)"
                />
              </Grid>
            </Grid>
          </CardContent>
          
          <CardActions sx={{ p: 2 }}>
            <Button
              variant="contained"
              onClick={handleExtractPreview}
              disabled={false}
              startIcon={<PlayArrow />}
            >
              Extract Preview
            </Button>
          </CardActions>
        </Card>
      )}

      {/* Step 2: Extract Data Preview */}
      {activeStep === 1 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Step 2: Data Extraction Preview
            </Typography>
            
            {isExtracting ? (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" gutterBottom>
                  Extracting data from CLZ...
                </Typography>
                <LinearProgress 
                  variant="determinate" 
                  value={extractionProgress} 
                  sx={{ mt: 1 }}
                />
                <Typography variant="caption" color="text.secondary">
                  {extractionProgress}% complete
                </Typography>
              </Box>
            ) : (() => {
              console.log('Rendering step 2 - extractedData:', extractedData);
              console.log('extractedData.length:', extractedData.length);
              return extractedData.length > 0;
            })() ? (
              <Box>
                <Alert severity="success" sx={{ mb: 2 }}>
                  Successfully extracted {extractedData.length} movies from CLZ!
                </Alert>
                
                {extractionResult?.summary && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    <Typography variant="body2">
                      <strong>Summary:</strong> {extractionResult.summary.new} new movies, {extractionResult.summary.willUpdate} existing movies will be updated, {extractionResult.summary.noUpdatesNeeded} existing movies need no updates
                    </Typography>
                  </Alert>
                )}
                
                <Typography variant="subtitle1" gutterBottom>
                  Preview of extracted data:
                </Typography>
                
                <List>
                  {paginatedData.map((movie, index) => (
                    <ListItem key={index} divider>
                      <ListItemIcon>
                        {movie.isDuplicate ? (
                          movie.hasChanges ? (
                            <Box sx={{ color: 'info.main', fontSize: '1.2rem' }}>
                              🔄
                            </Box>
                          ) : (
                            <Box sx={{ color: 'text.secondary', fontSize: '1.2rem' }}>
                              ⏭️
                            </Box>
                          )
                        ) : (
                          <CheckCircle color="success" />
                        )}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {movie.title}
                            {movie.isDuplicate && (
                              <Chip 
                                label={
                                  movie.hasChanges 
                                    ? `Will Update (${movie.fieldChanges?.length || 0} changes)`
                                    : "No Updates Needed"
                                }
                                size="small" 
                                color={movie.hasChanges ? "info" : "default"}
                                variant="outlined"
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              {movie.year} • {movie.format} • {movie.barcode || 'No barcode'}
                            </Typography>
                            {movie.isDuplicate && movie.fieldChanges && movie.fieldChanges.length > 0 && (
                              <Box sx={{ mt: 1 }}>
                                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                                  Changes:
                                </Typography>
                                <Box sx={{ mt: 0.5 }}>
                                  {movie.fieldChanges.slice(0, 2).map((change, changeIndex) => (
                                    <Typography key={changeIndex} variant="caption" display="block" sx={{ fontSize: '0.75rem' }}>
                                      <strong>{change.field}:</strong> "{change.current}" → "{change.new}"
                                    </Typography>
                                  ))}
                                  {movie.fieldChanges.length > 2 && (
                                    <Typography variant="caption" sx={{ fontSize: '0.75rem', fontStyle: 'italic' }}>
                                      ... and {movie.fieldChanges.length - 2} more changes
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            )}
                          </Box>
                        }
                      />
                    </ListItem>
                  ))}
                </List>

                {/* Pagination */}
                {extractedData.length > previewPageSize && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Pagination
                      count={totalPages}
                      page={previewPage}
                      onChange={(event, value) => setPreviewPage(value)}
                      color="primary"
                      size="small"
                      showFirstButton
                      showLastButton
                    />
                  </Box>
                )}

                {/* Pagination Info */}
                {extractedData.length > 0 && (
                  <Box sx={{ textAlign: 'center', mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Showing {startIndex + 1}-{Math.min(endIndex, extractedData.length)} of {extractedData.length} movies
                    </Typography>
                  </Box>
                )}

                {/* Detailed Field Changes for Movies to be Updated */}
                {extractedData.some(movie => movie.isDuplicate && movie.hasChanges && movie.fieldChanges && movie.fieldChanges.length > 0) && (
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="h6" gutterBottom>
                      Detailed Field Changes
                    </Typography>
                    {extractedData
                      .filter(movie => movie.isDuplicate && movie.hasChanges && movie.fieldChanges && movie.fieldChanges.length > 0)
                      .slice(0, 3)
                      .map((movie, index) => (
                        <Accordion key={index} sx={{ mb: 1 }}>
                          <AccordionSummary expandIcon={<ExpandMore />}>
                            <Typography variant="subtitle2">
                              {movie.title} ({movie.fieldChanges.length} changes)
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails>
                            <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                              {movie.fieldChanges.map((change, changeIndex) => (
                                <Box key={changeIndex} sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                  <Typography variant="subtitle2" color="primary" gutterBottom>
                                    {change.field}
                                  </Typography>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                    <Box>
                                      <Typography variant="caption" color="text.secondary">
                                        Current:
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontFamily: 'monospace', bgcolor: 'error.light', p: 0.5, borderRadius: 0.5 }}>
                                        {change.current}
                                      </Typography>
                                    </Box>
                                    <Box>
                                      <Typography variant="caption" color="text.secondary">
                                        New:
                                      </Typography>
                                      <Typography variant="body2" sx={{ fontFamily: 'monospace', bgcolor: 'success.light', p: 0.5, borderRadius: 0.5 }}>
                                        {change.new}
                                      </Typography>
                                    </Box>
                                  </Box>
                                </Box>
                              ))}
                            </Box>
                          </AccordionDetails>
                        </Accordion>
                      ))}
                    {extractedData.filter(movie => movie.isDuplicate && movie.hasChanges && movie.fieldChanges && movie.fieldChanges.length > 0).length > 3 && (
                      <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                        ... and {extractedData.filter(movie => movie.isDuplicate && movie.hasChanges && movie.fieldChanges && movie.fieldChanges.length > 0).length - 3} more movies with changes
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            ) : (
              <Alert severity="warning">
                No data extracted. Please check your credentials and try again.
              </Alert>
            )}
          </CardContent>
          
          <CardActions sx={{ p: 2 }}>
            <Button
              variant="outlined"
              onClick={() => setActiveStep(0)}
            >
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handleMigration}
              disabled={extractedData.length === 0}
              startIcon={<CloudDownload />}
            >
              Start Migration
            </Button>
          </CardActions>
        </Card>
      )}

      {/* Step 3: Migration Progress */}
      {activeStep === 2 && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Step 3: Migrating to Database
            </Typography>
            
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress size={60} />
              <Typography variant="h6" sx={{ mt: 2 }}>
                Migrating your CLZ collection...
              </Typography>
              <Typography variant="body2" color="text.secondary">
                This may take several minutes depending on your collection size.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Step 4: Review Results */}
      {activeStep === 3 && migrationResult && (
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Step 4: Migration Results
            </Typography>
            
            {migrationResult.error ? (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="subtitle2">
                  Migration failed: {migrationResult.error}
                </Typography>
              </Alert>
            ) : (
              <Box>
                <Alert severity="success" sx={{ mb: 3 }}>
                  <Typography variant="subtitle2">
                    Migration completed successfully!
                  </Typography>
                  <Typography variant="body2">
                    Migrated {migrationResult.saved || migrationResult.migrated} items
                    {migrationResult.duplicatesSkipped > 0 && (
                      <span> ({migrationResult.duplicatesSkipped} existing entries updated)</span>
                    )}
                    {migrationResult.errors > 0 && (
                      <span> ({migrationResult.errors} errors)</span>
                    )}
                  </Typography>
                </Alert>

                {migrationResult.details && (
                  <Accordion>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                      <Typography variant="subtitle1">
                        Migration Details
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2" gutterBottom>
                            Successfully Migrated:
                          </Typography>
                          <List dense>
                            {migrationResult.details.migratedItems?.map((item, index) => (
                              <ListItem key={index}>
                                <ListItemIcon>
                                  <CheckCircle color="success" />
                                </ListItemIcon>
                                <ListItemText
                                  primary={item.title}
                                  secondary={`${item.year} • ${item.format} • ${item.barcode || 'No barcode'}`}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Grid>
                        
                        {migrationResult.details.errors?.length > 0 && (
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" gutterBottom color="error">
                              Errors:
                            </Typography>
                            <List dense>
                              {migrationResult.details.errors.map((error, index) => (
                                <ListItem key={index}>
                                  <ListItemIcon>
                                    <Error color="error" />
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={error.title}
                                    secondary={error.error}
                                  />
                                </ListItem>
                              ))}
                            </List>
                          </Grid>
                        )}
                      </Grid>
                    </AccordionDetails>
                  </Accordion>
                )}
              </Box>
            )}
          </CardContent>
          
          <CardActions sx={{ p: 2 }}>
            <Button
              variant="outlined"
              onClick={() => {
                setActiveStep(0);
                setMigrationResult(null);
                setExtractedData([]);
              }}
            >
              Start New Migration
            </Button>
            <Button
              variant="contained"
              onClick={() => navigate('/physical-media')}
            >
              View Collection
            </Button>
          </CardActions>
        </Card>
      )}

    </Box>
  );
};

export default CLZMigration;
